/**
 * The onboarding Modal's state, held outside React (`spec-onboarding-persistent-scene-launch-into-live`,
 * R2), as `planHandover.ts` holds the handover's: the page writes it through `useOnboardingModal`,
 * and `OnboardingModalLayer`, in the protected layout above the routes, renders it. So a summary
 * the landing opens stays the same open Modal across the route change into the live viewer.
 *
 * What it holds: the pending request and its one resolver slot (launch-modals R7), the activating
 * content held across an accepted changes check (spinner R3.2), the page's activating phase, the
 * model comparison, and whether the request is *carried* (the landing's summary, which outlives the
 * page). Every change replaces the snapshot, so `useSyncExternalStore` sees it.
 *
 * Nothing here is stored, logged or sent anywhere (spec, Access and Data Exposure). Imports types
 * only, so the layer that reads it adds no onboarding code to the main chunk (R2.4).
 */
import type React from 'react';
import type { ModelDiff } from '@repo/front-end-utils';

import type { DeploymentSummary } from './LaunchSuccessSummary';
import type { ComparisonStatus } from './ModelComparison';

/** What the summary shows, and what its *Compare models* control does. */
export interface SummaryOptions {
  title: string;
  summary: DeploymentSummary;
  /** The field-level diff (UPDATE only); null on a first launch. */
  diff?: ModelDiff | null;
  /** _Done_, _Open Floor Plan_ or _Go to twin_. */
  confirmLabel: string;
  /** Starts the comparison's read. Absent on a first launch: no _Compare models_ (R4). */
  onCompare?: () => void;
  /** The comparison was left, back to the summary: its read, if still out, is now stale (R8). */
  onCompareClosed?: () => void;
  /**
   * The confirm button runs this instead of settling the summary: the landing's retry after the
   * open failed (spec, Failure States). Escape and the close button still settle it.
   */
  onConfirmInstead?: () => void;
}

/** The comparison's two models and the "after" read's progress. */
export interface ComparisonView {
  before: unknown;
  after: unknown;
  status: ComparisonStatus;
  error?: string;
}

/** Where a Go Live run has got to: before the live twin changes, or changing it. */
export type ActivationPhase = 'checking' | 'changing';

/**
 * What the onboarding Modal shows. One union so every content, including Launch's `activating`
 * (`onboarding-persistent-scene-spinner`), is one more kind of the same Modal, never a second Modal.
 */
export type OnboardingModalState =
  | { kind: 'changes'; message: React.ReactNode }
  | ({ kind: 'summary' | 'comparison' } & SummaryOptions)
  | { kind: 'leave' }
  | { kind: 'activating'; phase: ActivationPhase };

export type RequestState = Exclude<OnboardingModalState, { kind: 'activating' }>;

export type Outcome = 'confirm' | 'cancel';

export interface OnboardingModalSnapshot {
  request: RequestState | null;
  /** The activating content held across an accepted changes check (spinner R3.2). */
  held: ActivationPhase | null;
  /** The page's Go Live phase while the wizard is locked for it. */
  activating: ActivationPhase | null;
  comparison: ComparisonView | null;
  /** The request outlives the page (R2.2): only the landing's summary. */
  carried: boolean;
}

const EMPTY: OnboardingModalSnapshot = {
  request: null,
  held: null,
  activating: null,
  comparison: null,
  carried: false,
};

let snapshot: OnboardingModalSnapshot = EMPTY;
let resolver: ((outcome: Outcome) => void) | null = null;
let lastPhase: ActivationPhase = 'checking';
const listeners = new Set<() => void>();

const write = (next: Partial<OnboardingModalSnapshot>) => {
  snapshot = { ...snapshot, ...next };
  listeners.forEach(listener => listener());
};

export const subscribeOnboardingModal = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getOnboardingModal = (): OnboardingModalSnapshot => snapshot;

/** What the Modal shows now: a pending request always wins over the activating content (R3.1). */
export const shownOf = (s: OnboardingModalSnapshot): OnboardingModalState | null => {
  if (s.request) return s.request;
  const phase = s.activating ?? s.held;
  return phase ? { kind: 'activating', phase } : null;
};

/**
 * Claim the single resolver slot (launch-modals R7): a second request while one is pending would
 * otherwise leave the first promise unsettled for ever. The outgoing request settles as cancel,
 * which every caller reads as "do nothing" (a summary reads it as acknowledged).
 */
export function openRequest(
  request: RequestState,
  resolve: (outcome: Outcome) => void,
  { carry = false }: { carry?: boolean } = {},
): void {
  const previous = resolver;
  resolver = resolve;
  // A new request replaces any held activating content (release c).
  write({ request, held: null, carried: carry });
  previous?.('cancel');
}

/** The person answered. Accepting the changes check goes on into the launch; nothing else does. */
export function settleRequest(outcome: Outcome): void {
  const resolve = resolver;
  resolver = null;
  const held = snapshot.request?.kind === 'changes' && outcome === 'confirm' ? lastPhase : null;
  write({ request: null, held, carried: false });
  resolve?.(outcome);
}

/** The confirm button: the request's own `onConfirmInstead` when it has one, else a settle. */
export function confirmRequest(): void {
  const request = snapshot.request;
  if (request && (request.kind === 'summary' || request.kind === 'comparison')) {
    if (request.onConfirmInstead) {
      request.onConfirmInstead();
      return;
    }
  }
  settleRequest('confirm');
}

/** Changes the open summary in place (the landing's relabel after a failed open), carried or not. */
export function updateSummary(
  changes: Partial<SummaryOptions>,
  { carry }: { carry?: boolean } = {},
): void {
  const request = snapshot.request;
  if (!request || (request.kind !== 'summary' && request.kind !== 'comparison')) return;
  write({
    request: { ...request, ...changes },
    ...(carry === undefined ? {} : { carried: carry }),
  });
}

/** The summary and the comparison are one open Modal changing content; its promise is untouched. */
export function showComparison(): void {
  const request = snapshot.request;
  if (request?.kind !== 'summary') return;
  write({ request: { ...request, kind: 'comparison' } });
  request.onCompare?.();
}

export function backToSummary(): void {
  const request = snapshot.request;
  if (request?.kind !== 'comparison') return;
  write({ request: { ...request, kind: 'summary' } });
  request.onCompareClosed?.();
}

/** The page's Go Live phase. A phase arriving takes over from any held content (release a). */
export function setActivatingPhase(phase: ActivationPhase | null): void {
  if (phase) lastPhase = phase;
  if (phase === snapshot.activating && (!phase || snapshot.held === null)) return;
  write(phase ? { activating: phase, held: null } : { activating: null });
}

/** The run is over (release b): a superseded run never re-locks, so nothing may stay held. */
export function endActivatingContent(): void {
  if (snapshot.held === null) return;
  write({ held: null });
}

export function setModalComparison(
  next: ComparisonView | null | ((current: ComparisonView | null) => ComparisonView | null),
): void {
  const value = typeof next === 'function' ? next(snapshot.comparison) : next;
  if (value === snapshot.comparison) return;
  write({ comparison: value });
}

/**
 * The page unmounted (browser back, a session-expiry redirect, the landing's own navigate). A
 * pending request settles as cancel, so the awaiting function's `finally` runs (R7), unless it is
 * carried (R2.2). The page's activating phase and held content go with the page.
 */
export function releaseForPageUnmount(): void {
  if (snapshot.carried) {
    if (snapshot.activating !== null || snapshot.held !== null) {
      write({ activating: null, held: null });
    }
    return;
  }
  const pending = resolver;
  resolver = null;
  write({ request: null, activating: null, held: null, carried: false });
  pending?.('cancel');
}

/**
 * A carried summary is released, closed with no fade, when the signed-in user changes or the
 * protected layout unmounts (R2.3), so it never shows over another session.
 */
export function releaseCarried(): void {
  if (!snapshot.carried) return;
  const pending = resolver;
  resolver = null;
  write({ request: null, carried: false, comparison: null });
  pending?.('cancel');
}

/** For tests: forget everything. */
export function resetOnboardingModalForTests(): void {
  resolver = null;
  lastPhase = 'checking';
  snapshot = EMPTY;
  listeners.forEach(listener => listener());
}
